// src/pages/Dashboard.js
import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import "./Dashboard.css";

const INACTIVITY_LIMIT = 15 * 60 * 1000;

/* ── email HTML builder (matches contention page style) ── */

export default function Dashboard() {
  const [penalties, setPenalties] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("combined");
  const [selectedPenalty, setSelectedPenalty] = useState(null);

  const [form, setForm] = useState({
    name: "",
    rollNo: "",
    email: "",
    role: "",
    remarks: "",
    clauses: [],
    penaltyText: "",
    penalties: {},
  });

  const API_BASE =
    "https://us-central1-anonymous-complaint-portal.cloudfunctions.net/poini";

  /* ── inactivity logout ── */
  useEffect(() => {
    let timeout;
    const reset = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        alert("Session expired due to inactivity");
        handleLogout();
      }, INACTIVITY_LIMIT);
    };
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timeout);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  /* ── api wrapper ── */
  const apiFetch = async (url, options = {}) => {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    if (res.status === 401) {
      alert("Session expired. Please login again.");
      await handleLogout();
      return null;
    }
    return res.json();
  };

  /* ── fetch ── */
  useEffect(() => {
    fetchPenalties();
  }, []);

  const fetchPenalties = async () => {
    const snapshot = await getDocs(collection(db, "penalties"));
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setPenalties(data);
    setFiltered(data);
  };

  /* ── filter ── */
  useEffect(() => {
    let temp = penalties;
    if (search) {
      temp = temp.filter(
        (p) =>
          (p.rollNo || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.penaltyId || "").toLowerCase().includes(search.toLowerCase()),
      );
    }
    if (statusFilter !== "all")
      temp = temp.filter((p) => p.status === statusFilter);
    setFiltered(temp);
  }, [search, statusFilter, penalties]);

  /* ── logout ── */
  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("otpVerified");
    window.location.href = "/";
  };

  /* ── clauses ── */
  const addClause = () =>
    setForm((p) => ({
      ...p,
      clauses: [...p.clauses, { clauseId: "", text: "" }],
    }));

  const updateClause = (i, field, val) => {
    const updated = [...form.clauses];
    updated[i][field] = val;
    setForm((p) => ({ ...p, clauses: updated }));
  };

  const removeClause = (i) => {
    const updated = [...form.clauses];
    const removed = updated[i];
    delete form.penalties[removed.clauseId];
    updated.splice(i, 1);
    setForm((p) => ({ ...p, clauses: updated }));
  };

  const setPenaltyVal = (clauseId, val) =>
    setForm((p) => ({ ...p, penalties: { ...p.penalties, [clauseId]: val } }));

  /* ── validate ── */
  const validate = () => {
    if (!form.name || !form.rollNo || !form.email || !form.role) {
      alert("All fields required");
      return false;
    }
    if (form.clauses.length === 0) {
      alert("Add at least one clause");
      return false;
    }
    for (const c of form.clauses) {
      if (!c.clauseId) {
        alert("Clause ID required");
        return false;
      }
    }
    if (mode === "combined" && !form.penaltyText) {
      alert("Penalty required");
      return false;
    }
    if (mode === "individual") {
      for (const c of form.clauses) {
        if (!form.penalties[c.clauseId]) {
          alert(`Penalty missing for ${c.clauseId}`);
          return false;
        }
      }
    }
    return true;
  };

  /* ── payload ── */
  const buildPayload = () => ({
    ...form,
    penaltyMode: mode,
    penalties:
      mode === "individual"
        ? form.clauses.map((c) => ({
            clauseId: c.clauseId,
            penaltyText: form.penalties[c.clauseId],
          }))
        : form.penalties,
    status: "pending",
    contention: false,
    createdAt: Date.now(),
    expiresAt: Date.now() + 3 * 24 * 60 * 60 * 1000,
  });

  /* ── submit ── */
  const handleSubmit = async (doEmail) => {
    if (!validate()) return;
    const res = await apiFetch(`${API_BASE}/create-penalty`, {
      method: "POST",
      body: JSON.stringify({
        uid: auth.currentUser.uid,
        data: buildPayload(),
        sendEmail: doEmail,
      }),
    });
    if (!res) return;

    if (doEmail && res.penaltyId) {
      await apiFetch(`${API_BASE}/generate-contention-link`, {
        method: "POST",
        body: JSON.stringify({
          uid: auth.currentUser.uid,
          penaltyId: res.penaltyId,
        }),
      });
    }

    alert(
      doEmail
        ? "Penalty added · Email sent · Contention link sent"
        : "Penalty added",
    );
    setShowForm(false);
    fetchPenalties();
  };

  /* ── send email from modal ── */
  const sendEmail = async (penalty) => {
    const res = await apiFetch(`${API_BASE}/send-penalty-email`, {
      method: "POST",
      body: JSON.stringify({
        uid: auth.currentUser.uid,
        penaltyId: penalty.penaltyId,
      }),
    });
    if (!res) return;

    await apiFetch(`${API_BASE}/generate-contention-link`, {
      method: "POST",
      body: JSON.stringify({
        uid: auth.currentUser.uid,
        penaltyId: penalty.penaltyId,
      }),
    });

    alert("Penalty email + Contention link sent");
    setSelectedPenalty(null);
    fetchPenalties();
  };

  /* ── stats ── */
  const total = penalties.length;
  const pending = penalties.filter((p) => p.status === "pending").length;
  const finalized = penalties.filter((p) => p.status === "finalized").length;
  const disputed = penalties.filter((p) => p.contention).length;

  /* ── ui ── */
  return (
    <div className="dashboard-container">
      {/* header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          {/* <div className="dash-seal">EC</div> */}
          <div>
            <h2>Penalty Dashboard</h2>
            <div className="dash-sub">Election Commission, IIT Kanpur</div>
          </div>
        </div>
        <button className="btn-logout" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* stats */}
      <div className="stats">
        <div className="stat-card">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{finalized}</div>
          <div className="stat-label">Finalized</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{disputed}</div>
          <div className="stat-label">Disputed</div>
        </div>
      </div>

      {/* controls */}
      <div className="controls">
        <input
          placeholder="Search by Roll No / Penalty ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="finalized">Finalized</option>
        </select>
        <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "✕ Close Form" : "+ Create Penalty"}
        </button>
      </div>

      {/* create form */}
      {showForm && (
        <div className="form-box">
          <h3>Create Penalty</h3>

          <div className="mode-switch">
            <button
              className={mode === "combined" ? "active" : ""}
              onClick={() => setMode("combined")}
            >
              Combined
            </button>
            <button
              className={mode === "individual" ? "active" : ""}
              onClick={() => setMode("individual")}
            >
              Individual
            </button>
          </div>

          <input
            placeholder="Name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            placeholder="Roll No"
            onChange={(e) => setForm({ ...form, rollNo: e.target.value })}
          />
          <input
            placeholder="Email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            placeholder="Role"
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          />
          <textarea
            placeholder="Remarks (optional)"
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          />

          <button
            className="btn-primary"
            style={{ marginBottom: 12 }}
            onClick={addClause}
          >
            + Add Clause
          </button>

          {form.clauses.map((c, idx) => (
            <div key={idx} className="selected-clause">
              <input
                placeholder="Clause ID"
                onChange={(e) => updateClause(idx, "clauseId", e.target.value)}
              />
              <input
                placeholder="Clause Text"
                onChange={(e) => updateClause(idx, "text", e.target.value)}
              />
              {mode === "individual" && (
                <input
                  placeholder="Penalty"
                  onChange={(e) => setPenaltyVal(c.clauseId, e.target.value)}
                />
              )}
              <button className="btn-remove" onClick={() => removeClause(idx)}>
                ✕
              </button>
            </div>
          ))}

          {mode === "combined" && (
            <textarea
              placeholder="Combined Penalty"
              onChange={(e) =>
                setForm({ ...form, penaltyText: e.target.value })
              }
            />
          )}

          <div className="form-actions">
            <button className="btn-primary" onClick={() => handleSubmit(false)}>
              Add Only
            </button>
            <button className="btn-primary" onClick={() => handleSubmit(true)}>
              Add + Send Email &amp; Contention Link
            </button>
          </div>
        </div>
      )}

      {/* table */}
      <div className="table">
        <div className="table-header">
          <span>Penalty ID</span>
          <span>Name</span>
          <span>Roll No</span>
          <span>Status</span>
          <span>Email</span>
          <span>Contention</span>
        </div>

        {filtered.map((p) => (
          <div
            className="table-row"
            key={p.id}
            onClick={() => setSelectedPenalty(p)}
          >
            <span>{p.penaltyId}</span>
            <span>{p.name}</span>
            <span>{p.rollNo}</span>
            <span>
              <span className={`status ${p.status}`}>{p.status}</span>
            </span>
            <span
              className={`email-status ${p.emailSent ? "sent" : "not-sent"}`}
            >
              {p.emailSent ? "Sent" : "Not Sent"}
            </span>
            <span className={p.contention ? "contention-yes" : "contention-no"}>
              {p.contention ? "Yes" : "No"}
            </span>
          </div>
        ))}
      </div>

      {/* modal */}
      {selectedPenalty && (
        <div
          className="modal-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setSelectedPenalty(null)
          }
        >
          <div className="modal-box">
            <h3>Penalty Details</h3>

            <p>
              <b>ID:</b> {selectedPenalty.penaltyId}
            </p>
            <p>
              <b>Name:</b> {selectedPenalty.name}
            </p>
            <p>
              <b>Roll No:</b> {selectedPenalty.rollNo}
            </p>
            <p>
              <b>Email:</b> {selectedPenalty.email}
            </p>
            <p>
              <b>Role:</b> {selectedPenalty.role}
            </p>
            <p>
              <b>Status:</b> {selectedPenalty.status}
            </p>
            <p>
              <b>Remarks:</b> {selectedPenalty.remarks || "None"}
            </p>

            <h4>Clauses</h4>
            <ul>
              {selectedPenalty.clauses?.map((c, i) => (
                <li key={i}>
                  {c.clauseId} — {c.text}
                </li>
              ))}
            </ul>

            {selectedPenalty.penaltyMode === "combined" && (
              <>
                <h4>Penalty</h4>
                <p>{selectedPenalty.penaltyText}</p>
              </>
            )}

            {selectedPenalty.penaltyMode === "individual" && (
              <>
                <h4>Penalties</h4>
                <ul>
                  {selectedPenalty.penalties?.map((p, i) => (
                    <li key={i}>
                      {p.clauseId} → {p.penaltyText}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {selectedPenalty.contention &&
              selectedPenalty.contentionHistory?.length > 0 && (
                <>
                  <h4>Contention History</h4>
                  {selectedPenalty.contentionHistory.map((h, i) => (
                    <div
                      key={i}
                      style={{
                        background: "var(--paper)",
                        border: "1px solid var(--border)",
                        padding: "10px 12px",
                        marginBottom: 8,
                        fontSize: 14,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: 10,
                          color: "var(--ink-3)",
                          marginBottom: 4,
                        }}
                      >
                        {new Date(h.createdAt).toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata",
                        })}
                      </div>
                      <div style={{ color: "var(--ink-2)", lineHeight: 1.6 }}>
                        {h.message}
                      </div>
                    </div>
                  ))}
                </>
              )}

            <div className="modal-actions">
              <button
                disabled={selectedPenalty.emailSent}
                onClick={() => sendEmail(selectedPenalty)}
              >
                {selectedPenalty.emailSent
                  ? "Email Already Sent"
                  : "Send Email + Contention Link"}
              </button>
              <button
                className="btn-close"
                onClick={() => setSelectedPenalty(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
